import { motion } from 'framer-motion';
import { Copy, Clipboard, ArrowRight, RotateCcw, CheckCircle2 } from 'lucide-react';

const VP = { once: true, margin: "-60px" };
const EASE = [0.16, 1, 0.3, 1];

const OLD_STEPS = [
  { num: 1, text: 'Open ChatGPT tab #1', sub: '"How do I write a good prompt for creating a business plan?"', icon: '💬', color: 'text-white/30' },
  { num: 2, text: 'Copy the 300-word prompt GPT writes', sub: null, icon: null, iconEl: Copy, color: 'text-white/25' },
  { num: 3, text: 'Open ChatGPT tab #2', sub: 'New empty chat. Feels wasteful.', icon: '🗂', color: 'text-white/25' },
  { num: 4, text: 'Paste the prompt', sub: null, icon: null, iconEl: Clipboard, color: 'text-white/20' },
  { num: 5, text: 'Get a result. Maybe good. Maybe not.', sub: 'Go back to step 1 if not.', icon: null, iconEl: RotateCcw, color: 'text-amber-500/60' },
];

const NEW_STEPS = [
  { num: 1, text: 'You type what you need — one line', sub: '"business plan for my AI startup"', glow: true },
  { num: 2, text: 'SendPI detects your platform', sub: 'Builds the precision cast automatically', glow: true },
  { num: 3, text: 'Result delivered', sub: null, glow: true, done: true },
];

const PAIN_POINTS = [
  { icon: '🔁', title: 'The copy-paste loop', desc: 'Open a chat to write a prompt for the chat you actually need. Copy it. Paste it. Repeat when it doesn\'t work.' },
  { icon: '💸', title: 'Paying twice', desc: 'Every meta-prompt call costs tokens and time. You\'re paying your AI to teach you how to use your AI.' },
  { icon: '🎲', title: 'The quality lottery', desc: "GPT's prompt of your prompt might be great. Or generic. You won't know until you've already submitted it." },
];

export function MetaPromptSection() {
  return (
    <section className="py-28 md:py-36 relative overflow-hidden" data-testid="meta-prompt-section">
      {/* Background: warm/red on left, violet/cyan on right */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-[15%] -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-[0.04]"
          style={{ background: 'radial-gradient(circle, #EF4444 0%, transparent 70%)' }} />
        <div className="absolute top-1/2 right-[15%] -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-[0.05]"
          style={{ background: 'radial-gradient(circle, #8A2BE2 0%, transparent 70%)' }} />
      </div>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

      <div className="relative z-[2] max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div className="mb-16 max-w-3xl" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={VP} transition={{ duration: 0.7, ease: EASE }}>
          <span className="text-red-400/70 text-[11px] font-semibold uppercase tracking-[3px] block mb-5">The problem with how you use AI today</span>
          <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
            Stop asking GPT to write<br /><span className="text-white/40">your GPT prompt.</span>
          </h2>
          <p className="text-white/45 max-w-xl text-base mt-5 font-light leading-relaxed">
            Every day, millions of people open a second chat just to write a better prompt for the first chat. Then copy. Then paste. Then hope. <span className="text-white/70">SendPI makes that workflow extinct.</span>
          </p>
        </motion.div>

        {/* Two-flow comparison */}
        <div className="grid lg:grid-cols-[1fr,auto,1fr] gap-6 lg:gap-4 mb-16">
          {/* LEFT — Old way */}
          <motion.div
            className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 relative"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={VP}
            transition={{ duration: 0.6, ease: EASE }}
          >
            <div className="text-white/30 text-xs font-semibold uppercase tracking-widest mb-5">What you do today</div>
            <div className="space-y-3">
              {OLD_STEPS.map((step, i) => (
                <motion.div
                  key={step.num}
                  className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/[0.04]"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.4 }}
                >
                  <span className="text-white/15 text-xs font-bold w-5 shrink-0 mt-0.5">{step.num}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${step.color}`}>{step.text}</p>
                    {step.sub && <p className="text-white/15 text-xs mt-0.5 italic">{step.sub}</p>}
                  </div>
                  {step.icon && <span className="text-sm opacity-30">{step.icon}</span>}
                  {step.iconEl && <step.iconEl className="w-4 h-4 text-white/15 shrink-0 mt-0.5" />}
                </motion.div>
              ))}
            </div>
            {/* Loop arrow */}
            <motion.div
              className="mt-3 flex items-center justify-center gap-2 text-amber-500/40"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span className="text-[10px] font-medium">Repeat until frustrated</span>
            </motion.div>
            <div className="mt-4 text-center">
              <p className="text-white/20 text-[10px] font-medium">3 tabs · 5 steps · 2 chats · 300 wasted tokens · still not sure it'll work</p>
            </div>
          </motion.div>

          {/* VS divider */}
          <div className="flex lg:flex-col items-center justify-center py-4 lg:py-0">
            <div className="w-12 h-12 rounded-full bg-white/[0.04] border border-white/[0.08] flex items-center justify-center">
              <span className="text-white/20 text-sm font-bold">vs</span>
            </div>
          </div>

          {/* RIGHT — SendPI way */}
          <motion.div
            className="rounded-2xl border border-[#8A2BE2]/30 p-6 relative"
            style={{ background: 'linear-gradient(135deg, rgba(138,43,226,0.06) 0%, rgba(34,211,238,0.03) 100%)' }}
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={VP}
            transition={{ duration: 0.6, ease: EASE }}
          >
            <div className="text-[#C084FC] text-xs font-semibold uppercase tracking-widest mb-5">What SendPI does</div>
            <div className="space-y-3">
              {NEW_STEPS.map((step, i) => (
                <motion.div
                  key={step.num}
                  className="flex items-start gap-3 p-3 rounded-lg border"
                  style={{
                    background: step.done ? 'rgba(16,185,129,0.06)' : 'rgba(138,43,226,0.04)',
                    borderColor: step.done ? 'rgba(16,185,129,0.2)' : 'rgba(138,43,226,0.15)',
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.15, duration: 0.4 }}
                >
                  <span className="text-[#C084FC] text-xs font-bold w-5 shrink-0 mt-0.5">{step.num}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/80 text-sm font-medium">{step.text}</p>
                    {step.sub && <p className="text-white/40 text-xs mt-0.5">{step.sub}</p>}
                  </div>
                  {step.done && <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />}
                  {step.glow && !step.done && <ArrowRight className="w-4 h-4 text-[#C084FC]/50 shrink-0 mt-0.5" />}
                </motion.div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <p className="text-emerald-400/70 text-[10px] font-semibold">1 tab · 1 line · 0 wasted tokens · guaranteed structure</p>
            </div>
          </motion.div>
        </div>

        {/* Pain points */}
        <div className="grid sm:grid-cols-3 gap-4 mb-16">
          {PAIN_POINTS.map((p, i) => (
            <motion.div
              key={p.title}
              className="glass-card rounded-xl p-5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5, ease: EASE }}
            >
              <span className="text-2xl mb-3 block">{p.icon}</span>
              <h3 className="text-white font-bold text-sm mb-2">{p.title}</h3>
              <p className="text-white/40 text-sm leading-relaxed font-light">{p.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Closing statement */}
        <motion.div
          className="text-center max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={VP}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <p className="font-outfit text-xl sm:text-2xl md:text-3xl font-bold tracking-tight leading-snug">
            <span className="pi-gradient-text">SendPI replaces the meta-prompt entirely.</span>
          </p>
          <p className="text-white/50 text-base mt-3 font-light">You state the outcome. We build the cast. One tab. One step. Every time.</p>
        </motion.div>
      </div>
    </section>
  );
}
